'use client';

import workingHours from './working-hours.json';
import { sendChatGTMEvent } from '@/components/helpers/analytics/gtm';
import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { showGenesysAtom } from '@/state/atoms';

interface IGenesisEvent {
  time: number;
  data: { message?: { text?: string } };
  event: string;
  eventName: string;
  publisher: string;
}

const CHAT_LOCAL_STORAGE_KEY = 'chat_ls_key';

function getEasternTime(now: Date) {
  const estString = now.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
  });
  return new Date(estString);
}

function initGenesysChat() {
  const deploymentId = process.env.NEXT_PUBLIC_GENESYS_DEPLOYMENT_ID;
  const environment = process.env.NEXT_PUBLIC_GENESYS_ENV;

  if (window.Genesys) {
    console.warn('Genesys is already started');
    return;
  }
  if (!deploymentId || !environment) {
    console.warn('Genesys config missing in env vars.');
    return;
  }

  (function (g, e, n, es) {
    g['_genesysJs'] = e;
    g[e] =
      g[e] ||
      function () {
        (g[e].q = g[e].q || []).push(arguments);
      };
    g[e].t = Date.now();
    g[e].c = es;

    const script = document.createElement('script');
    script.async = true;
    script.src = n;
    script.charset = 'utf-8';

    script.onload = () => {
      window.Genesys('subscribe', 'Messenger.closed', (_e: IGenesisEvent) => {
        if (typeof window !== 'undefined' && window.localStorage) {
          const interactLocation =
            localStorage.getItem(CHAT_LOCAL_STORAGE_KEY) || 'global';
          localStorage.removeItem(CHAT_LOCAL_STORAGE_KEY);
          sendChatGTMEvent('closed chat', interactLocation);
        }
      });

      window.Genesys('subscribe', 'Messenger.opened', (_e: IGenesisEvent) => {
        if (typeof window !== 'undefined' && window.localStorage) {
          const interactLocation =
            localStorage.getItem(CHAT_LOCAL_STORAGE_KEY) || 'global';
          localStorage.removeItem(CHAT_LOCAL_STORAGE_KEY);
          sendChatGTMEvent('opened chat', interactLocation);
        }
      });

      window.Genesys(
        'subscribe',
        'MessagingService.sendingMessage',
        (_e: IGenesisEvent) => {
          sendChatGTMEvent(
            'sent message',
            document.location.pathname,
            _e.data?.message?.text,
          );
        },
      );
    };

    document.head.appendChild(script);
  })(
    window as any,
    'Genesys',
    'https://apps.usw2.pure.cloud/genesys-bootstrap/genesys.min.js',
    { environment, deploymentId },
  );
}

export default function GenesysLoader() {
  const [_, setShowGenesys] = useAtom(showGenesysAtom);

  useEffect(() => {
    if (typeof window === 'undefined' || window.Genesys) return;

    const now = new Date();
    const nowEST = getEasternTime(now);
    const dayName = now.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long',
    });

    const todaysHours = (
      workingHours as Record<string, { start: string; end: string } | null>
    )[dayName];

    if (!todaysHours) {
      console.info(`Chat is disabled today (${dayName}).`);
      setShowGenesys(false);
      return;
    }

    const [startHour, startMinute] = todaysHours.start.split(':').map(Number);
    const [endHour, endMinute] = todaysHours.end.split(':').map(Number);

    const start = new Date(nowEST);
    start.setHours(startHour, startMinute, 0, 0);

    const end = new Date(nowEST);
    end.setHours(endHour, endMinute, 0, 0);

    if (nowEST < start) {
      const timeUntilStart = start.getTime() - nowEST.getTime();
      console.info(
        `Chat will open in ${Math.round(timeUntilStart / 1000)} seconds (${todaysHours.start})`,
      );
      setShowGenesys(false);

      setTimeout(() => {
        console.info('Opening Genesys chat at start of working hours.');
        initGenesysChat();
        setShowGenesys(true);
      }, timeUntilStart);
      return;
    }

    if (nowEST > end) {
      console.info(
        `Chat is currently closed. Now: ${nowEST.toTimeString()}, Hours: ${todaysHours.start}–${todaysHours.end}`,
      );
      setShowGenesys(false);
      return;
    }

    initGenesysChat();
    setShowGenesys(true);

    const timeUntilEnd = end.getTime() - nowEST.getTime();
    console.info(
      `Chat will be removed in ${Math.round(timeUntilEnd / 1000)} seconds (${todaysHours.end})`,
    );

    setTimeout(() => {
      removeChat();
      setShowGenesys(false);
    }, timeUntilEnd);
  }, []);

  return null;
}

export function openChat(interactLocation: string) {
  if (typeof window !== 'undefined' && window.Genesys && window.localStorage) {
    window.Genesys('command', 'Messenger.open');
    localStorage.setItem(CHAT_LOCAL_STORAGE_KEY, interactLocation);
  }
}

export function removeChat() {
  const messenger = document.getElementById('genesys-messenger');
  if (messenger && messenger.parentNode) {
    messenger.parentNode.removeChild(messenger);
    console.info('Genesys chat removed after business hours.');
  }
}

export function hideChat() {
  if (typeof window !== 'undefined' && window.Genesys) {
    window.Genesys('command', 'Messenger.close');
  }
}
